import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from "../utils/AxiosHelper";
import APIs from "../modules/settings/APIs";
import {useAppSelector} from "./hooks";
import {API_PARAMS} from "../constants/constants";

export type ScaleBand = {
  id: number;
  groupId: number;
  label: string;
  color: string;
  scoreMin: number;
  scoreMax: number;
  formula: string | null;
  state: string;
  displayOrder: number | null;
};

type ScaleBandParams = {
  type?: string;
  state?: string;
}

const fetchScaleBands = async (params: ScaleBandParams): Promise<ScaleBand[]> => {
  const response = await apiRequest.get(APIs.parameters.config.assessment.scaleBands, {
    params: {
      type: params.type || API_PARAMS.IMPACT.ISSUES_ENTITY,
      state: params.state || API_PARAMS.IS_ACTIVE
    }
  });
  return response.data.data;
};

export const useScaleBands = (params: ScaleBandParams = {}) => {
  const {isAuthenticated, token} = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: ['scaleBands', params.type, params.state],
    queryFn: () => fetchScaleBands(params),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    enabled: isAuthenticated && !!token
  });
};

export type UpdateScaleBandPayload = Partial<Pick<ScaleBand, 'label' | 'color' | 'formula' | 'scoreMin' | 'scoreMax' | 'displayOrder' | 'state'>>;

export const useUpdateScaleBand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({id, data}: {id: string | number; data: UpdateScaleBandPayload}) => {
      const response = await apiRequest.put(`${APIs.parameters.config.assessment.scaleBands}/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['scaleBands']});
    },
  });
};

export type CreateScaleBandPayload = {
  label: string;
  color: string;
  formula?: string | null;
  displayOrder?: number | null;
  groupId?: number;
};

export const useDeleteScaleBand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await apiRequest.delete(`${APIs.parameters.config.assessment.scaleBands}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['scaleBands']});
    },
  });
};

export const useCreateScaleBand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateScaleBandPayload) => {
      const response = await apiRequest.post(
        APIs.parameters.config.assessment.scaleBands,
        data,
        {
          params: {
            type: API_PARAMS.IMPACT.ISSUES_ENTITY,
            state: API_PARAMS.IS_ACTIVE,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['scaleBands']});
    },
  });
};
